// What a job's type is called outside the Studio's own code (spec docs/specs/web/G1-screens.md
// decision 30): the library's ⓘ names the generation a file came from, and the library may not
// import the Studio's type modules (`platform/studio/types`). Each name is the module's
// `label` (live's list word), so the two read the same; `type-names.test.ts` in the Studio holds
// them equal. Live's label map lacks Video Edit, Video Extend and Lip-Sync, so their lists show
// no word; ⓘ names them by their tool. `clone` marks a type whose job the Studio's composer can
// be filled from (`?clone=`, as the job's Clone): the Image page's three and the Video page's
// three modes; every other type has a form or a tool of its own.
import type { GenerationType } from "../../api/generated/model";

export interface TypeName {
  readonly name: string;
  readonly clone: boolean;
}

export const TYPE_NAMES: { readonly [T in GenerationType]: TypeName } = {
  "audio.extract": { name: "Audio", clone: false },
  "audio.music": { name: "Music", clone: false },
  "audio.sfx": { name: "Sound effects", clone: false },
  "audio.tts": { name: "Text-to-speech", clone: false },
  "audio.voiceover": { name: "Voiceover", clone: false },
  "caption.transcribe": { name: "Captions", clone: false },
  // The brand and product forms' extraction from a URL (#249): read where it was asked for.
  "extract.brand": { name: "Brand from a website", clone: false },
  "extract.product": { name: "Product from a store", clone: false },
  "image.actor": { name: "AI Actor", clone: false },
  "image.advanced": { name: "Advanced", clone: true },
  "image.analysis": { name: "Image analysis", clone: false },
  "image.layer_edit": { name: "AI layer edit", clone: false },
  "image.layerize": { name: "Layerize", clone: false },
  "image.simple": { name: "Simple", clone: true },
  "image.template": { name: "Template", clone: false },
  "image.tweak": { name: "Tweak", clone: true },
  "text.actor_description": { name: "Actor description", clone: false },
  "text.ad_copy": { name: "Ad copies", clone: false },
  "text.enhance": { name: "Script enhancement", clone: false },
  "text.generate": { name: "Text", clone: false },
  "text.script": { name: "Script", clone: false },
  "text.shot_list": { name: "Shot list", clone: false },
  "video.audio": { name: "Video audio", clone: false },
  "video.background": { name: "Edit Background", clone: false },
  "video.caption": { name: "Captions", clone: false },
  "video.dub": { name: "Video Dubbing", clone: false },
  "video.edit": { name: "Video Edit", clone: false },
  "video.extend": { name: "Video Extend", clone: false },
  "video.frames": { name: "Video frames", clone: false },
  "video.image": { name: "Image-to-Video", clone: true },
  "video.lipsync": { name: "Lip-Sync", clone: false },
  "video.merge": { name: "Merge", clone: false },
  "video.motion": { name: "Motion Control", clone: false },
  "video.overlay": { name: "Overlay", clone: false },
  "video.reference": { name: "Reference-to-Video", clone: true },
  "video.reframe": { name: "Reframe", clone: false },
  "video.shots": { name: "Video shots", clone: false },
  "video.splice": { name: "Splice", clone: false },
  "video.pixel_snap": { name: "Pixel snap", clone: false },
  "video.talking_head": { name: "Simple Talking Head", clone: false },
  "video.talking_head_advanced": { name: "Advanced Talking Head", clone: false },
  "video.template": { name: "Template", clone: false },
  "video.text": { name: "Text-to-Video", clone: true },
  "video.upscale": { name: "Video Upscale", clone: false },
  "video.voice_swap": { name: "Voice swap", clone: false },
  "voice.clone": { name: "Voice clone", clone: false },
  "voice.design": { name: "Voice design", clone: false },
  "voice.profile": { name: "Voice profile", clone: false },
};

/**
 * Whether a job type is a generation's, whose job `GET /jobs/{id}` reads: a saved file's origin
 * names its job's type (`AssetJobOrigin.job_type`), which may be another kind of job (an editor's
 * render) or none once the job was deleted, and then nothing asks for the job.
 */
export function isGenerationType(type: string | null): type is GenerationType {
  return type !== null && Object.hasOwn(TYPE_NAMES, type);
}
